import {
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  Icon,
  LayerCatalogError,
  LayerLibraryTable,
  ScopeTabs,
  SELECTED_LAYERS_TABLE_WIDTH,
  SelectedLayersTable,
  Text,
} from '@civilgrid/ui/components';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import { handleForbiddenResource } from '~/common/authUtils';
import { useAvailableLayers } from '~/hooks/useAvailableLayers';
import { useOrgLayerConfigSync } from '~/hooks/useOrgLayerConfigSync';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import orgLayerConfigStore, { canEditOrgLayerConfig } from '~/store2/orgLayerConfig';
import { DIGITIZED_SECTION_KEY } from '../../layerLibrary/digitizedLibraryGroup';
import { type LayerLibraryTab, resetDialogCopy } from '../../layerLibrary/helpers';
import { useLayerLibraryCatalog } from '../../layerLibrary/useLayerLibraryCatalog';
import {
  trackOrgLayerLibraryLayerAdded,
  trackOrgLayerLibraryLayerRemoved,
  trackOrgLayerLibraryOpened,
  trackOrgLayerLibraryReset,
  trackOrgLayerLibraryScopeChanged,
  trackOrgLayerLibrarySearched,
} from './orgLayerLibraryEvents';

// The org dialog always resets to CivilGrid defaults — no org fallback to name; static args,
// so this evaluates once at module load rather than per render.
const ORG_RESET_COPY = resetDialogCopy({ scope: 'org' });

/**
 * Org-admin "Default Layer Configuration" page (CG-724). Owner-only: an org's default
 * layer set is what a member's Layer Panel/Library resets to. Membership-only editor —
 * no visibility/pin state, unlike the per-user Layer Library page it mirrors structurally.
 */
export default function OrgLayerLibrary() {
  const selectedHeadingId = useId();
  const { currentOrganizationId } = useSnapshot(rootStore) as typeof rootStore;
  const canManage = authorizations.can(Action.ManageOrgLayerConfig);

  const { loadState, libraryLayerIds, meta, catalogVersion, saveError } = useSnapshot(orgLayerConfigStore);
  const { tilesConfig, customLayersTilesConfig } = useSnapshot(layerStore) as typeof layerStore;
  const canEdit = canEditOrgLayerConfig(loadState) && canManage;

  const [tab, setTab] = useState<LayerLibraryTab>('civilgrid');
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  // Guard: not an Owner → redirect. (Server PUT is already owner-gated;
  // this is a UX guard because the settings shell only checks ViewOrgSettings.)
  useEffect(() => {
    if (!canManage) handleForbiddenResource();
  }, [canManage]);

  // Catalog, bootstrap and sync are withheld for a non-Owner about to be redirected.
  const catalogQuery = useAvailableLayers(canManage ? currentOrganizationId : undefined);

  // Load org defaults; re-fire on org change (refetch-on-open for multi-admin last-write-wins).
  useEffect(() => {
    if (canManage && currentOrganizationId) void orgLayerConfigStore.bootstrap(currentOrganizationId);
  }, [canManage, currentOrganizationId]);

  useEffect(() => {
    if (catalogQuery.data) orgLayerConfigStore.setCatalog(catalogQuery.data, currentOrganizationId);
  }, [catalogQuery.data, currentOrganizationId]);

  // Icon kinds/colors derive from the Map's session-cached tiles config. Bootstrap fetches it on
  // app start; the memoized fetch makes this a no-op then, and a real (single) request only when
  // the page is hit before bootstrap resolves. Until it arrives, rows render the default icons.
  useEffect(() => {
    layerStore.fetchTilesConfig();
  }, []);
  // Debounced auto-save lifecycle: start on mount; flush on tab-hide/close + unmount, stop on unmount.
  useOrgLayerConfigSync(canManage);

  // Fire "opened" once, when access first resolves to allowed — a non-owner about to be
  // redirected never emits it.
  const openedTrackedRef = useRef(false);
  useEffect(() => {
    if (canManage && !openedTrackedRef.current) {
      openedTrackedRef.current = true;
      trackOrgLayerLibraryOpened(currentOrganizationId);
    }
  }, [canManage, currentOrganizationId]);

  const catalog = catalogQuery.data;
  const { constituentKeys, catalogRows, selectedRows, tabOptions, getLayerInfo } = useLayerLibraryCatalog({
    catalog,
    libraryLayerIds,
    tab,
    tilesConfig,
    customLayersTilesConfig,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot fields stand in for the store reads inside isAtDefaultLayers
  const isAtDefaults = useMemo(() => orgLayerConfigStore.isAtDefaultLayers(), [meta, libraryLayerIds, catalogVersion]);

  // The synthetic Digitized Layers row fans out to its real constituent groups — the store's
  // membership rebuild then reflects the section through the constituents' catalog leaves.
  const handleAddLayer = (rowId: string) => {
    if (!canEdit) return;
    const groupIds = rowId === DIGITIZED_SECTION_KEY ? constituentKeys : [rowId];
    for (const groupId of groupIds) orgLayerConfigStore.addToLibrary(groupId);
    trackOrgLayerLibraryLayerAdded(rowId, tab, currentOrganizationId);
  };
  const handleRemoveLayer = (rowId: string) => {
    if (!canEdit) return;
    const groupIds = rowId === DIGITIZED_SECTION_KEY ? constituentKeys : [rowId];
    for (const groupId of groupIds) orgLayerConfigStore.removeFromLibrary(groupId);
    trackOrgLayerLibraryLayerRemoved(rowId, tab, currentOrganizationId);
  };
  const handleResetLayers = () => {
    if (!canEdit) return;
    const removedCount = selectedRows.length;
    orgLayerConfigStore.resetToDefaultLayers();
    trackOrgLayerLibraryReset(removedCount, currentOrganizationId);
    setIsResetDialogOpen(false);
  };
  const handleScopeChange = (next: LayerLibraryTab) => {
    if (next === tab) return;
    trackOrgLayerLibraryScopeChanged(tab, next, currentOrganizationId);
    setTab(next);
  };

  // Stable so the table's debounced search effect isn't reset on every render.
  const handleSearch = useCallback(() => {
    trackOrgLayerLibrarySearched(currentOrganizationId);
  }, [currentOrganizationId]);

  if (!canManage) return <CircularProgress indeterminate />;

  return (
    <div className="flex h-full w-full flex-col gap-4" data-testid="org-layer-library-page">
      <div className="flex flex-col gap-1">
        <Text className="text-lg font-semibold text-primary">Default Layer Configuration</Text>
        <Text className="text-sm text-grey-70">
          Choose which layers from the Layer Library show up by default in the Layer Panel. When a user resets their
          layers to default, this is what their view resets to.
        </Text>
      </div>

      <ScopeTabs
        options={tabOptions}
        value={tab}
        onValueChange={(value) => handleScopeChange(value as LayerLibraryTab)}
        orientation="horizontal"
        data-testid="org-layer-library-scope-tabs"
      />

      {saveError && (
        <Text className="text-sm text-danger" data-testid="org-layer-library-save-error">
          Couldn't save changes — we'll retry on your next edit.
        </Text>
      )}

      {loadState === 'loading' && <Text data-testid="org-layer-library-settings-loading">Loading org defaults…</Text>}
      {loadState === 'failed' && (
        <div className="flex items-center gap-2" data-testid="org-layer-library-settings-failed">
          <Text className="text-sm text-danger">Couldn't load org defaults.</Text>
          <Button
            size="small"
            onClick={() => currentOrganizationId && orgLayerConfigStore.bootstrap(currentOrganizationId)}
            data-testid="org-layer-library-settings-retry"
          >
            Retry
          </Button>
        </div>
      )}

      <div className="flex min-h-0 grow flex-row gap-4">
        <div className="flex min-h-0 min-w-0 grow flex-col">
          {catalogQuery.isError ? (
            <LayerCatalogError onRetry={() => catalogQuery.refetch()} testIdPrefix="org-layer-library-catalog" />
          ) : (
            <LayerLibraryTable
              aria-label="Layer library"
              rows={catalogRows}
              onAddLayer={canEdit ? (row) => handleAddLayer(row.id) : undefined}
              onSearch={handleSearch}
              getLayerInfo={getLayerInfo}
              isLoading={catalogQuery.isLoading}
              emptyState={<Text data-testid="org-layer-library-catalog-empty">No layers available.</Text>}
              id="org-layer-library-catalog"
              className="h-full min-h-0 min-w-0"
            />
          )}
        </div>
        <div className="flex min-h-0 shrink-0 flex-col gap-2" style={{ width: SELECTED_LAYERS_TABLE_WIDTH }}>
          <div className="flex h-8 items-center justify-between">
            <div className="flex items-center gap-2">
              <Text id={selectedHeadingId} className="text-base font-semibold text-primary">
                Selected
              </Text>
              <Badge kind="count" size="small">
                {selectedRows.length.toLocaleString()}
              </Badge>
            </div>
            <Button
              variant="danger"
              size="small"
              disabled={!canEdit || isAtDefaults}
              onClick={() => setIsResetDialogOpen(true)}
              data-testid="org-layer-library-reset-layers"
            >
              <Icon icon="reset" size={14} /> Reset to CivilGrid defaults
            </Button>
          </div>
          <SelectedLayersTable
            aria-labelledby={selectedHeadingId}
            rows={selectedRows}
            isLoading={catalogQuery.isLoading}
            onRemove={canEdit ? (row) => handleRemoveLayer(row.id) : undefined}
            getLayerInfo={getLayerInfo}
            emptyState={<Text data-testid="org-layer-library-selected-empty">No layers selected.</Text>}
            id="org-layer-library-selected-layers"
            className="min-h-0 flex-1"
          />
        </div>
      </div>

      {/* Destructive-action confirmation (CG-725 Figma wording) — always names CivilGrid. */}
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent data-testid="org-layer-library-reset-dialog" title={ORG_RESET_COPY.title}>
          <DialogBody>
            <Text className="text-sm text-grey-90">{ORG_RESET_COPY.body}</Text>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsResetDialogOpen(false)}
              data-testid="org-layer-library-reset-cancel"
            >
              {ORG_RESET_COPY.cancel}
            </Button>
            <Button variant="danger" onClick={handleResetLayers} data-testid="org-layer-library-reset-confirm">
              {ORG_RESET_COPY.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
